// ---------------------------------------------------------------------------
// GiftCard Renderer (editor key: GiftCard)
// ---------------------------------------------------------------------------
// Display-only commerce primitive — publishes the editor `GiftCard` block as
// a static list of amount options plus a purchase CTA. The editor preview
// embeds inputs and click handlers; the published page does NOT — checkout
// behavior lives on the storefront route, not in static HTML. We only render
// the visual structure so the block survives publish and the operator can
// style it from the theme. Amounts are parsed from a comma-separated string,
// trimmed, and clamped to a sane count to prevent layout overflow.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text } from "../escape.js";

interface GiftCardProps {
  title?: string;
  amounts?: string;
  currency?: string;
  buttonLabel?: string;
}

const MAX_AMOUNTS = 8;

function parseAmounts(value: unknown): string[] {
  if (typeof value !== "string") return [];
  return value
    .split(",")
    .map((amount) => amount.trim())
    .filter((amount) => amount.length > 0 && amount.length <= 16)
    .slice(0, MAX_AMOUNTS);
}

function normalizeCurrency(value: unknown): string {
  if (typeof value !== "string") return "€";
  const trimmed = value.trim();
  // Only allow short non-numeric currency symbols to avoid HTML injection
  if (!trimmed || trimmed.length > 4) return "€";
  return trimmed;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as GiftCardProps;

  const title = typeof p.title === "string" ? p.title.trim() : "";
  const amounts = parseAmounts(p.amounts);
  if (!title && amounts.length === 0) {
    return "<!-- gift-card missing title and amounts -->";
  }

  const buttonLabel = typeof p.buttonLabel === "string" && p.buttonLabel.trim()
    ? p.buttonLabel.trim()
    : "Purchase Gift Card";
  const currency = normalizeCurrency(p.currency);

  const titleHtml = title
    ? `\n    <h3 class="ease-gift-card__title">${text(title)}</h3>`
    : "";

  const amountItems = amounts
    .map((amount, index) => {
      const isFeatured = index === 1;
      const className = isFeatured
        ? "ease-gift-card__amount ease-gift-card__amount--featured"
        : "ease-gift-card__amount";
      return `      <li class="${attr(className)}"><span class="ease-gift-card__currency">${text(currency)}</span><span class="ease-gift-card__value">${text(amount)}</span></li>`;
    })
    .join("\n");

  const amountsHtml = amounts.length > 0
    ? `\n    <ul class="ease-gift-card__amounts" role="list">\n${amountItems}\n    </ul>`
    : "";

  return `<section class="ease-section ease-gift-card" aria-label="${attr(title || "Gift card")}">
  <div class="ease-gift-card__frame">${titleHtml}${amountsHtml}
    <p class="ease-gift-card__cta-row"><span class="ease-gift-card__cta ease-btn ease-btn-primary" role="button" tabindex="0">${text(buttonLabel)}</span></p>
  </div>
</section>`;
}

registerRenderer("gift-card", { render });
